import {
  faChartBar,
  faImage,
  faSync,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HorizontalBar } from "react-chartjs-2";
import { useIntl } from "react-intl";
import { Button, Col, Input, Row, Spinner, Table } from "reactstrap";
import {
  ADMIN_AI_USAGE_USE_MOCK,
  AdminAiImageUsageRow,
  fetchAdminAiImageUsage,
} from "../../../cores/helpers/services/admin-ai-image-usage";
import { useBodyDarkMode } from "../../../hooks/useBodyDarkMode";

const PALETTE = [
  "rgba(129, 140, 248, 0.92)",
  "rgba(34, 211, 238, 0.88)",
  "rgba(251, 191, 36, 0.9)",
  "rgba(167, 139, 250, 0.9)",
  "rgba(59, 130, 246, 0.9)",
  "rgba(244, 114, 182, 0.88)",
  "rgba(45, 212, 191, 0.9)",
  "rgba(251, 113, 133, 0.88)",
];

const CHART_TOP = 14;

type SortKey = "displayName" | "email" | "images";

function truncateLabel(s: string, max = 26): string {
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

function errorKey(e: unknown): "notFound" | "forbidden" | "generic" {
  if (axios.isAxiosError(e)) {
    const st = e.response?.status;
    if (st === 404) return "notFound";
    if (st === 401 || st === 403) return "forbidden";
  }
  return "generic";
}

export function WembiiAdminAiUsageSection() {
  const intl = useIntl();
  const isDark = useBodyDarkMode();
  const [rows, setRows] = useState<AdminAiImageUsageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorKind, setErrorKind] = useState<"notFound" | "forbidden" | "generic" | null>(
    null
  );
  const [searchFilter, setSearchFilter] = useState("");
  const [minImagesStr, setMinImagesStr] = useState("");
  const [maxImagesStr, setMaxImagesStr] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({
    key: "images",
    dir: "desc",
  });

  const tickColor = isDark ? "#ffffff" : "#475569";
  const gridColor = isDark ? "rgba(148, 163, 184, 0.12)" : "rgba(148, 163, 184, 0.25)";

  const load = useCallback(async () => {
    setLoading(true);
    setErrorKind(null);
    try {
      const data = await fetchAdminAiImageUsage();
      setRows(data);
    } catch (e) {
      setRows([]);
      setErrorKind(errorKey(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredSortedRows = useMemo(() => {
    let list = [...rows];
    const q = searchFilter.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (r) =>
          r.displayName.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) ||
          r.userId.toLowerCase().includes(q)
      );
    }
    const minN = minImagesStr.trim() === "" ? null : Number(minImagesStr);
    const maxN = maxImagesStr.trim() === "" ? null : Number(maxImagesStr);
    if (minN !== null && Number.isFinite(minN)) {
      list = list.filter((r) => r.totalImages >= minN);
    }
    if (maxN !== null && Number.isFinite(maxN)) {
      list = list.filter((r) => r.totalImages <= maxN);
    }

    const dirMul = sort.dir === "asc" ? 1 : -1;
    const collatorLocale = intl.locale;
    list.sort((a, b) => {
      if (sort.key === "images") {
        return dirMul * (a.totalImages - b.totalImages);
      }
      const va = sort.key === "email" ? a.email : a.displayName;
      const vb = sort.key === "email" ? b.email : b.displayName;
      return (
        dirMul *
        va.localeCompare(vb, collatorLocale, {
          sensitivity: "base",
          numeric: true,
        })
      );
    });
    return list;
  }, [rows, searchFilter, minImagesStr, maxImagesStr, sort, intl.locale]);

  const toggleSort = useCallback((key: SortKey) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: key === "images" ? "desc" : "asc" };
    });
  }, []);

  const clearFilters = useCallback(() => {
    setSearchFilter("");
    setMinImagesStr("");
    setMaxImagesStr("");
  }, []);

  const chartSlice = useMemo(() => {
    const top = filteredSortedRows.slice(0, CHART_TOP);
    return {
      labels: top.map((r) => truncateLabel(r.displayName)),
      values: top.map((r) => r.totalImages),
    };
  }, [filteredSortedRows]);

  const barColors = useMemo(
    () => chartSlice.labels.map((_, i) => PALETTE[i % PALETTE.length]),
    [chartSlice.labels]
  );

  const horizontalBarData = useMemo(
    () => ({
      labels: chartSlice.labels,
      datasets: [
        {
          label: intl.formatMessage({ id: "app.adminDashboard.aiUsage.chartDataset" }),
          data: chartSlice.values,
          backgroundColor: barColors,
          borderColor: "rgba(255,255,255,0.35)",
          borderWidth: 1,
        },
      ],
    }),
    [barColors, chartSlice.labels, chartSlice.values, intl]
  );

  const horizontalBarOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      responsive: true,
      animation: { duration: 720 },
      legend: { display: false },
      scales: {
        xAxes: [
          {
            ticks: {
              beginAtZero: true,
              precision: 0,
              fontColor: tickColor,
              fontFamily:
                'var(--wembii-font-sans, "Inter", system-ui, sans-serif)',
            },
            gridLines: {
              color: gridColor,
              zeroLineColor: gridColor,
            },
          },
        ],
        yAxes: [
          {
            barPercentage: 0.72,
            categoryPercentage: 0.78,
            ticks: {
              fontColor: tickColor,
              fontFamily:
                'var(--wembii-font-sans, "Inter", system-ui, sans-serif)',
            },
            gridLines: { display: false },
          },
        ],
      },
      tooltips: {
        backgroundColor: isDark ? "rgba(15, 23, 42, 0.94)" : "rgba(255, 255, 255, 0.98)",
        titleFontColor: isDark ? "#ffffff" : "#0f172a",
        bodyFontColor: isDark ? "#ffffff" : "#334155",
        borderColor: "rgba(99, 102, 241, 0.4)",
        borderWidth: 1,
      },
      layout: { padding: { left: 8, right: 12, top: 8, bottom: 6 } },
    }),
    [gridColor, isDark, tickColor]
  );

  const sortBadge = useCallback(
    (key: SortKey) => {
      if (sort.key !== key) return "";
      if (key === "images") {
        return sort.dir === "asc"
          ? intl.formatMessage({ id: "app.adminDashboard.aiUsage.sortImagesAsc" })
          : intl.formatMessage({ id: "app.adminDashboard.aiUsage.sortImagesDesc" });
      }
      return sort.dir === "asc"
        ? intl.formatMessage({ id: "app.adminDashboard.aiUsage.sortAz" })
        : intl.formatMessage({ id: "app.adminDashboard.aiUsage.sortZa" });
    },
    [intl, sort.dir, sort.key]
  );

  const pillName = sortBadge("displayName");
  const pillEmail = sortBadge("email");
  const pillImages = sortBadge("images");

  const errorMessage =
    errorKind === "notFound"
      ? intl.formatMessage({ id: "app.adminDashboard.aiUsage.errorNotFound" })
      : errorKind === "forbidden"
        ? intl.formatMessage({ id: "app.adminDashboard.aiUsage.errorForbidden" })
        : errorKind === "generic"
          ? intl.formatMessage({ id: "app.adminDashboard.aiUsage.errorGeneric" })
          : null;

  return (
    <section
      className="wembii-admin-ai-usage mb-5"
      aria-labelledby="wembii-admin-ai-usage-title"
    >
      <div className="wembii-admin-ai-usage__glow" />
      <div className="wembii-admin-ai-usage__inner">
        <div className="wembii-admin-ai-usage__head">
          <div className="wembii-admin-ai-usage__title-block">
            <span className="wembii-admin-ai-usage__badge">
              <FontAwesomeIcon icon={faWandMagicSparkles} className="mr-1" />
              {intl.formatMessage({ id: "app.adminDashboard.aiUsage.badge" })}
            </span>
            <h4 id="wembii-admin-ai-usage-title" className="wembii-admin-ai-usage__title">
              {intl.formatMessage({ id: "app.adminDashboard.aiUsage.title" })}
            </h4>
            <p className="wembii-admin-ai-usage__subtitle small mb-0">
              {intl.formatMessage({ id: "app.adminDashboard.aiUsage.subtitle" })}
            </p>
          </div>
          <Button
            color="link"
            className="wembii-admin-ai-usage__refresh p-0"
            onClick={load}
            disabled={loading}
            type="button"
          >
            {loading ? (
              <Spinner size="sm" className="mr-1" />
            ) : (
              <FontAwesomeIcon icon={faSync} className="mr-1" />
            )}
            {intl.formatMessage({ id: "app.adminDashboard.aiUsage.refresh" })}
          </Button>
        </div>

        {ADMIN_AI_USAGE_USE_MOCK && (
          <div className="wembii-admin-ai-usage__mock-banner" role="status">
            {intl.formatMessage({ id: "app.adminDashboard.aiUsage.mockBanner" })}
          </div>
        )}

        {errorMessage && (
          <div className="wembii-admin-ai-usage__alert" role="alert">
            {errorMessage}
          </div>
        )}

        {loading && rows.length === 0 && !errorKind && (
          <div className="wembii-admin-ai-usage__loading py-4 text-center">
            <Spinner color="primary" />
          </div>
        )}

        {!loading && !errorKind && rows.length === 0 && (
          <p className="wembii-admin-ai-usage__empty small mb-0">
            {intl.formatMessage({ id: "app.adminDashboard.aiUsage.empty" })}
          </p>
        )}

        {!errorKind && rows.length > 0 && (
          <>
            <div className="wembii-admin-ai-usage__filters">
              <Row className="wembii-admin-ai-usage__filter-row g-2 align-items-end">
                <Col xs="12" md="5" lg="4">
                  <label
                    className="wembii-admin-ai-usage__filter-label"
                    htmlFor="wembii-ai-usage-search"
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.aiUsage.filterSearch" })}
                  </label>
                  <Input
                    id="wembii-ai-usage-search"
                    bsSize="sm"
                    type="search"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    placeholder={intl.formatMessage({
                      id: "app.adminDashboard.aiUsage.filterSearchPlaceholder",
                    })}
                  />
                </Col>
                <Col xs="6" sm="4" md="3" lg="2">
                  <label
                    className="wembii-admin-ai-usage__filter-label"
                    htmlFor="wembii-ai-usage-min"
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.aiUsage.filterMin" })}
                  </label>
                  <Input
                    id="wembii-ai-usage-min"
                    bsSize="sm"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={minImagesStr}
                    onChange={(e) => setMinImagesStr(e.target.value)}
                    placeholder="0"
                  />
                </Col>
                <Col xs="6" sm="4" md="3" lg="2">
                  <label
                    className="wembii-admin-ai-usage__filter-label"
                    htmlFor="wembii-ai-usage-max"
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.aiUsage.filterMax" })}
                  </label>
                  <Input
                    id="wembii-ai-usage-max"
                    bsSize="sm"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={maxImagesStr}
                    onChange={(e) => setMaxImagesStr(e.target.value)}
                    placeholder="∞"
                  />
                </Col>
                <Col
                  xs="12"
                  sm="4"
                  md="auto"
                  className="wembii-admin-ai-usage__filter-actions mb-1 mb-md-0"
                >
                  <Button
                    color="link"
                    size="sm"
                    className="wembii-admin-ai-usage__filter-clear p-0"
                    type="button"
                    onClick={clearFilters}
                    disabled={
                      !searchFilter.trim() && !minImagesStr.trim() && !maxImagesStr.trim()
                    }
                  >
                    {intl.formatMessage({ id: "app.adminDashboard.aiUsage.filterClear" })}
                  </Button>
                </Col>
              </Row>
              <p className="wembii-admin-ai-usage__filter-hint small mb-0 mt-2">
                {intl.formatMessage(
                  { id: "app.adminDashboard.aiUsage.filterHint" },
                  {
                    shown: filteredSortedRows.length,
                    total: rows.length,
                  }
                )}
              </p>
            </div>

            {filteredSortedRows.length === 0 ? (
              <p className="wembii-admin-ai-usage__empty small mb-0 mt-3">
                {intl.formatMessage({ id: "app.adminDashboard.aiUsage.filterNoResults" })}
              </p>
            ) : (
              <>
                <div className="wembii-admin-ai-usage__chart-head">
                  <FontAwesomeIcon icon={faChartBar} className="mr-2" />
                  {intl.formatMessage(
                    { id: "app.adminDashboard.aiUsage.chartTitleExtended" },
                    {
                      show: Math.min(CHART_TOP, filteredSortedRows.length),
                      filtered: filteredSortedRows.length,
                    }
                  )}
                </div>
                <div className="wembii-admin-ai-usage__chart">
                  <HorizontalBar data={horizontalBarData} options={horizontalBarOptions} />
                </div>

                <div className="wembii-admin-ai-usage__table-wrap">
                  <div className="wembii-admin-ai-usage__table-caption small">
                    <FontAwesomeIcon icon={faImage} className="mr-1" />
                    {intl.formatMessage({ id: "app.adminDashboard.aiUsage.tableCaption" })}
                  </div>
                  <div className="table-responsive wembii-admin-table-wrap wembii-admin-ai-usage__table-shell">
                    <Table size="sm" className="wembii-admin-table mb-0">
                      <thead>
                        <tr>
                          <th
                            className="sortable"
                            onClick={() => toggleSort("displayName")}
                          >
                            <div className="wembii-admin-ai-usage__th-inner">
                              <span className="wembii-admin-ai-usage__th-text">
                                {intl.formatMessage({ id: "app.adminDashboard.aiUsage.colUser" })}
                              </span>
                              {pillName ? (
                                <span className="wembii-admin-ai-usage__sort-pill">{pillName}</span>
                              ) : null}
                            </div>
                          </th>
                          <th
                            className="sortable"
                            onClick={() => toggleSort("email")}
                          >
                            <div className="wembii-admin-ai-usage__th-inner">
                              <span className="wembii-admin-ai-usage__th-text">
                                {intl.formatMessage({ id: "app.adminDashboard.aiUsage.colEmail" })}
                              </span>
                              {pillEmail ? (
                                <span className="wembii-admin-ai-usage__sort-pill">{pillEmail}</span>
                              ) : null}
                            </div>
                          </th>
                          <th
                            className="sortable text-right"
                            onClick={() => toggleSort("images")}
                          >
                            <div className="wembii-admin-ai-usage__th-inner wembii-admin-ai-usage__th-inner--end">
                              <span className="wembii-admin-ai-usage__th-text">
                                {intl.formatMessage({ id: "app.adminDashboard.aiUsage.colImages" })}
                              </span>
                              {pillImages ? (
                                <span className="wembii-admin-ai-usage__sort-pill">{pillImages}</span>
                              ) : null}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSortedRows.map((r) => (
                          <tr key={`${r.userId}-${r.email}`}>
                            <td className="font-weight-medium">{r.displayName}</td>
                            <td className="small">{r.email}</td>
                            <td className="text-right wembii-admin-num font-weight-bold">
                              {intl.formatNumber(r.totalImages)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}
